import { Link } from "react-router-dom";

export default function HowToNegotiateSalary() {
    return (
        <>
            <p>
                Most people leave money on the table when they negotiate salary — not because they tried and failed, but because
                they didn't try at all. Research consistently shows that over 50% of people accept the first offer without
                negotiating, even though employers typically expect negotiation. Here's how to approach it with numbers
                instead of nerves.
            </p>

            <h2>Know Your Market Rate Before You Say Anything</h2>
            <p>
                Walking into a negotiation without knowing the market rate for your role is like buying a car without knowing
                what it costs. You need specific numbers before the conversation starts.
            </p>
            <p>
                Use multiple sources to triangulate: Glassdoor, LinkedIn Salary, Levels.fyi (for tech roles), the Bureau of
                Labor Statistics Occupational Outlook Handbook, and — most reliably — people in your professional network who
                do the same job. Job postings in states that require salary transparency (California, New York, Colorado,
                Washington) are also useful real-world data.
            </p>
            <p>
                Aim to know the 25th, 50th, and 75th percentile for your role, experience level, and geographic area. If you're
                being offered $72,000 and the 75th percentile for your role in your city is $88,000, you have a clear,
                defensible gap to close.
            </p>
            <p>
                Use the <Link to="/calculator/salary">salary calculator</Link> to understand how hourly, weekly, monthly, and
                annual figures convert, and to compare take-home pay across different offers or locations.
            </p>

            <h2>Calculate What the Difference Actually Means</h2>
            <p>
                People often think of salary differences in monthly terms — "$500/month more doesn't feel like much." But look
                at the cumulative impact:
            </p>
            <p>
                A $5,000 raise at age 30 isn't just $5,000. It raises your base for every future raise, bonus, and promotion
                that's calculated as a percentage of base. Over 10 years, a $5,000 initial gap — with 3% annual raises on top
                of it — compounds to about $57,000 in additional earnings. If your company also does percentage-based bonuses,
                the gap is even larger.
            </p>
            <p>
                This is why it's worth having a real conversation about base pay even when the dollar gap feels small.
                Use the <Link to="/calculator/compound-interest">compound interest calculator</Link> to project what
                the difference looks like invested at 7% over a career — usually $200K+ for a $5,000 starting gap.
            </p>

            <h2>Anchor high, then negotiate down — never the reverse</h2>
            <p>
                When asked "what's your expected salary?" — name a number 10–15% above the role's median. The worst they
                say is "we can do $X" — and that "$X" is almost always higher than what they would have offered
                unprompted. If you anchor low, you can never recover that ground.
            </p>

            <h2>Negotiate the whole package, not just base</h2>
            <p>
                If the company won't move on base salary, ask about: signing bonus, equity refresh, additional PTO,
                remote-work flexibility, a 6-month review with a built-in raise, professional development budget, or
                a higher 401(k) match. These are often easier "yes" decisions for the hiring manager than base salary.
            </p>

            <h2>Practice the conversation out loud</h2>
            <p>
                Negotiation anxiety is real — but it dissolves with rehearsal. Practice the exact words you'll say
                with a friend or in front of a mirror. "Based on my research, market for this role with my
                experience is $X to $Y. I'd be excited to accept at $Z." Say it 10 times until it feels boring. Then
                say it on the call.
            </p>

            <p>
                Compare offers across cities or filing statuses with our{" "}
                <Link to="/calculator/take-home/tx">take-home pay calculator</Link> to make sure you're comparing
                apples to apples.
            </p>
        </>
    );
}